import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { useFormatDate } from '@mdvip/shared/hooks/useFormatDate';
import { cn } from '@mdvip/shared/lib/utils';
import { useTranslation } from 'react-i18next';
import type { AwpStatus } from '@/hooks/member/awpStatusApi';

interface AwpStatusDatesProps {
  status: AwpStatus;
  className?: string;
}

/**
 * The member's last and next-eligible AWP dates as two plain label + value pairs. Neutral on
 * purpose: the dates are not statuses, so weight rather than color separates the visit behind from
 * the one ahead. A member with no history keeps both labels, with a screen-reader-only "Not on
 * record" in the value slot so a label is never left dangling.
 *
 * The `|` between the pairs is generated content on the second pair, shown only once the nearest
 * `@container/health-themes` ancestor is wide enough for the row not to wrap. Being generated, it
 * stays out of the accessibility tree. Each pair's icon lives inside its `<dt>`, so every group in
 * the `<dl>` holds only a `dt` and a `dd`.
 */
export function AwpStatusDates({ status, className }: AwpStatusDatesProps) {
  const { t } = useTranslation('members');
  const { formatDateMedium } = useFormatDate();

  return (
    <dl className={cn('flex flex-wrap items-center gap-x-4 gap-y-2 text-base', className)}>
      <DatePair
        icon="clockRotateLeft"
        label={t('pages.dashboard.awpStatus.lastAwp')}
        date={status.lastAwpDate}
        formatDate={formatDateMedium}
        notOnRecord={t('pages.dashboard.awpStatus.notOnRecord')}
      />
      <DatePair
        icon="calendarCheck"
        label={t('pages.dashboard.awpStatus.nextEligible')}
        date={status.nextAwpDueDate}
        formatDate={formatDateMedium}
        notOnRecord={t('pages.dashboard.awpStatus.notOnRecord')}
        divided
      />
    </dl>
  );
}

function DatePair({
  icon,
  label,
  date,
  formatDate,
  notOnRecord,
  divided = false,
}: {
  icon: 'clockRotateLeft' | 'calendarCheck';
  label: string;
  date: string | null;
  formatDate: (dateOnly: string) => string;
  notOnRecord: string;
  divided?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2',
        divided &&
          "before:mr-2 before:hidden before:text-border-strong before:content-['|'] @3xl/health-themes:before:inline",
      )}
    >
      <dt className="flex items-center gap-2 font-semibold text-muted-foreground">
        <Icon name={icon} size="sm" />
        {label}
      </dt>
      <dd className="font-semibold text-foreground">
        {date ? <time dateTime={date}>{formatDate(date)}</time> : <span className="sr-only">{notOnRecord}</span>}
      </dd>
    </div>
  );
}
